import React, { useState, useMemo, useEffect } from "react";
import { View, ScrollView, TextInput, Pressable, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { supabase } from "@/lib/supabase-client";

export type Location = {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  street: string;
  city: string;
  state: string;
  zip: string;
  instructions: string | null;
  meta_data: Record<string, any>;
  created_at: string;
  market_id: string | null;
};

export type LocationPickerProps = {
  onPick: (locationId: string) => void;
  onCancel?: () => void;
};

export function LocationPicker({ onPick, onCancel }: LocationPickerProps) {
  const { colors } = useTheme();
  const [search, setSearch] = useState("");
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch locations on mount
  useEffect(() => {
    let cancel = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .schema("lists")
          .from("locations")
          .select(
            "id, tenant_id, name, description, street, city, state, zip, instructions, meta_data, created_at, market_id",
          )
          .order("name", { ascending: true });

        if (cancel) return;

        if (fetchError) {
          console.error("Failed to fetch locations:", fetchError);
          setError("Failed to load locations");
          return;
        }

        setLocations(data ?? []);
      } catch (err) {
        if (!cancel) {
          console.error("Unexpected error fetching locations:", err);
          setError("An unexpected error occurred");
        }
      } finally {
        if (!cancel) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancel = true;
    };
  }, []);

  const filteredLocations = useMemo(() => {
    if (!search) return locations;
    const searchLower = search.toLowerCase();
    return locations.filter((location) => {
      const name = location.name.toLowerCase();
      const address = `${location.street} ${location.city} ${location.state} ${location.zip}`.toLowerCase();
      const description = location.description?.toLowerCase() || "";
      const instructions = location.instructions?.toLowerCase() || "";

      return (
        name.includes(searchLower) ||
        address.includes(searchLower) ||
        description.includes(searchLower) ||
        instructions.includes(searchLower)
      );
    });
  }, [locations, search]);

  const renderLocation = (location: Location) => {
    const fullAddress = `${location.street}, ${location.city}, ${location.state} ${location.zip}`;

    return (
      <Pressable
        key={location.id}
        testID={`location-option-${location.id}`}
        style={[styles.locationRow, { borderBottomColor: colors.border }]}
        onPress={() => onPick(location.id)}
      >
        <View style={styles.locationContent}>
          <ThemedText type="defaultSemiBold">{location.name}</ThemedText>
          <ThemedText style={[styles.locationDetail, { color: colors.textSecondary }]}>{fullAddress}</ThemedText>
          {location.description && (
            <ThemedText style={[styles.locationDetail, { color: colors.textSecondary }]}>
              {location.description}
            </ThemedText>
          )}
          {location.instructions && (
            <ThemedText style={[styles.locationDetail, { color: colors.textSecondary, fontStyle: "italic" }]}>
              Instructions: {location.instructions}
            </ThemedText>
          )}
        </View>
      </Pressable>
    );
  };

  return (
    <View testID="location-picker" style={styles.container}>
      <ThemedText type="subtitle">Select Location</ThemedText>

      <TextInput
        testID="location-picker-search"
        style={[
          styles.searchInput,
          {
            borderColor: colors.border,
            color: colors.text,
            backgroundColor: colors.backgroundSecondary,
          },
        ]}
        placeholder="Search by name, address, or description..."
        placeholderTextColor={colors.placeholder}
        value={search}
        onChangeText={setSearch}
        autoFocus
        editable={!loading}
      />

      <ScrollView style={styles.scrollView}>
        {loading ? (
          <ThemedText style={styles.message}>Loading locations...</ThemedText>
        ) : error ? (
          <ThemedText style={[styles.message, { color: colors.red.text }]}>{error}</ThemedText>
        ) : filteredLocations.length > 0 ? (
          filteredLocations.map(renderLocation)
        ) : (
          <ThemedText style={styles.message}>{search ? "No locations found" : "No locations available"}</ThemedText>
        )}
      </ScrollView>

      {onCancel && (
        <View style={styles.footer}>
          <Button
            testID="location-picker-cancel"
            text="Cancel"
            style={[
              styles.cancelButton,
              {
                borderColor: colors.border,
              },
            ]}
            textStyle={{ color: colors.text }}
            onPress={onCancel}
          />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 8,
    gap: 8,
  },
  searchInput: {
    height: 36,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  scrollView: {
    maxHeight: 350,
  },
  locationRow: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    backgroundColor: "transparent",
  },
  locationContent: {
    gap: 4,
  },
  locationDetail: {
    fontSize: 12,
  },
  message: {
    opacity: 0.7,
    alignSelf: "center",
    marginVertical: 20,
  },
  footer: {
    flexDirection: "row",
    gap: 8,
    justifyContent: "flex-end",
    marginTop: 8,
  },
  cancelButton: {
    height: 32,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
});
